import {ErrorBase} from "./ErrorBase";
import {StringHelper} from "../shared/StringHelper";
import {UrlHelper} from "../shared/UrlHelper";
import {ParcelDependencyContainer} from "./ParcelDependencyContainer";
import {DependencyContainerFactory} from "../dependencyContainerFactory";
import {Router} from "../router";
import {UrlSanitizer} from "../shared/UrlSanitizer";
import {IPresenter} from "../interfaces/IPresenter";

export abstract class ViewBase {
    public readonly _viewName: string;
    public $element: any;
    protected _dependencyContainer: ParcelDependencyContainer;
    protected _router: Router;

    constructor(viewName: string, router: Router, dependencyContainer: ParcelDependencyContainer) {
        this._viewName = viewName;
        this._router = router;
        this._dependencyContainer = dependencyContainer;
    }

    /**
     * Walks through all text nodes inside root and replaces {{expression}} tokens
     * with evaluated values using a controlled function scope (loop var + context).
     */
    private replaceTextNodes(
        root: HTMLElement,
        context: Record<string, any>,
        loopVar: string,
        loopValue: any
    ): void {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

        while (walker.nextNode()) {
            const node = walker.currentNode as Text;
            node.textContent = node.textContent?.replace(/\{\{\s*(.*?)\s*\}\}/g, (_, expr) => {
                try {
                    const argNames = [loopVar, ...Object.keys(context)];
                    const argValues = [loopValue, ...Object.values(context)];
                    const fn = new Function(...argNames, `return ${expr}`);
                    const result = fn(...argValues);
                    return String(result);
                } catch (e) {
                    console.warn(`Error evaluating expression "${expr}":`, e);
                    return '';
                }
            });
        }
    }

    private processOnce(context: Record<string, any>): boolean {
        const models = this.$element.querySelectorAll<HTMLElement>('[data-model]');
        const toProcess: HTMLElement[] = [];

        models.forEach(el => {
            let parent = el.parentElement;
            let insideAnotherModel = false;

            while (parent && parent !== this.$element) {
                if (parent.hasAttribute('data-model')) {
                    insideAnotherModel = true;
                    break;
                }
                parent = parent.parentElement;
            }

            if (!insideAnotherModel) {
                toProcess.push(el);
            }
        });

        if (toProcess.length === 0) return false;

        toProcess.forEach(el => {
            const model = el.dataset.model;
            if (!model) return;

            const match = model.match(/^\s*(\w+)\s+of\s+(.+?)\s*$/);
            if (!match) return;

            const [, loopVar, listExpr] = match;

            let list: any[];
            try {
                const fn = new Function(...Object.keys(context), `return ${listExpr}`);
                list = fn(...Object.values(context));
            } catch (e) {
                console.warn(`Error evaluating list expression "${listExpr}":`, e);
                return;
            }

            if (!Array.isArray(list)) {
                console.warn(`"${listExpr}" is not an array`);
                return;
            }

            const templateHTML = el.innerHTML;
            const parent = el.parentElement;
            if (!parent) return;

            el.remove();

            list.forEach(item => {
                const clone = document.createElement(el.tagName.toLowerCase());
                clone.innerHTML = templateHTML; // Set raw HTML structure
                this.replaceTextNodes(clone, context, loopVar, item); // Then safely replace tokens
                clone.querySelectorAll('*').forEach(child => {
                    this.replaceAttributeBindings(child as HTMLElement, context, loopVar, item);
                });
                parent.appendChild(clone);
            });
        });

        return true;
    }


    // const items = [
    //     { name: "Docs", link: "https://example.com/docs" },
    //     { name: "Bad",  link: "javascript:alert('xss')" } // will be blocked
    // ];
    private replaceAttributeBindings(
        element: HTMLElement,
        context: Record<string, any>,
        loopVar: string,
        loopValue: any,
        index: number = -1
    ) {
        const attributes = Array.from(element.attributes);

        attributes.forEach(attr => {
            const hasToken = /\{\{.*?\}\}/.test(attr.value);
            if (!hasToken) return;

            const newValue = attr.value.replace(/\{\{\s*(.*?)\s*\}\}/g, (_, expr) => {
                try {
                    const argNames = [loopVar, 'index', ...Object.keys(context)];
                    const argValues = [loopValue, index, ...Object.values(context)]

                    const fn = new Function(...argNames, `return ${expr}`);
                    return fn(...argValues);
                } catch (e) {
                    console.warn(`Failed to evaluate attribute expression "${expr}"`, e);
                    return '';
                }
            });

            const attrName = attr.name.toLowerCase();

            // Block inline event handlers like onclick, onload, etc.
            if (/^on/.test(attrName)) {
                console.warn(`Blocked unsafe attribute: ${attr.name}`);
                return;
            }

            // Sanitize URLs
            if (['href', 'src', 'xlink:href'].includes(attrName)) {
                const trimmed = newValue.trim().toLowerCase();
                if (trimmed.startsWith('javascript:') || trimmed.startsWith('data:text/html')) {
                    console.warn(`Blocked unsafe URL in ${attr.name}: ${newValue}`);
                    return;
                }
            }

            element.setAttribute(attr.name, newValue);
        });
    }

    public renderModelsUnder(context: Record<string, any>, tagName: string = null): void {
        this.processElementTree(this.$element, context, tagName);
    }

    private processElementTree(root: Element, context: Record<string, any>, tagName: string = null): void {
        const models = Array.from(root.querySelectorAll<HTMLElement>('[data-model]'));

        for (const el of models) {
            const model = el.dataset.model;
            if (!model) continue;

            const match = model.match(/^\s*(\w+)\s+of\s+(.+?)\s*$/);
            if (!match) continue;

            const [, loopVar, listExpr] = match;

            let list: any[];
            try {
                const fn = new Function(...Object.keys(context), `return ${listExpr}`);
                list = fn(...Object.values(context));
            } catch (e) {
                console.warn(`Error evaluating list expression "${listExpr}":`, e);
                continue;
            }

            if (!Array.isArray(list)) {
                console.warn(`"${listExpr}" is not an array`);
                continue;
            }

            const children = Array.from(el.children); // save child elements inside the template
            const parent = el.parentElement;
            if (!parent) continue;

            el.remove(); // remove the data-model container itself

            list.forEach((item, index) => {
                //const loopContext = { [loopVar]: item, index };

                for (const child of children) {
                    const clone = child.cloneNode(true) as HTMLElement;

                    this.replaceTextNodes(clone, {...context, index, tagName}, loopVar, item);
                    this.replaceAttributeBindings(clone, {...context, index, tagName}, loopVar, item);

                    clone.querySelectorAll('*').forEach(inner => {
                        this.replaceAttributeBindings(inner as HTMLElement, {
                            ...context,
                            index,
                            tagName
                        }, loopVar, item);
                    });

                    parent.insertBefore(clone, el.nextSibling);
                    this.processElementTree(clone, context); // recursive
                }
            });


        }
    }


    public attachComponent(presenter: IPresenter, cssSelector: string) {
        const el = this.$element.querySelector(cssSelector);
        el.append(presenter._view.$element);
    }

    public setElementVisibility(selector, value) {
        const element = this.$element.querySelector(selector);
        element.style.display = value ? '' : 'none';
    }

    public setElement(element: any) {
        this.$element = element;
    }

    public getValueById(elementId: string) {
        return (this.$element.querySelector(`#${elementId}`) as HTMLInputElement).value;
    }

    public setValueById(elementId: string, value: string) {
        (this.$element.querySelector(`#${elementId}`) as HTMLInputElement).value = value;
    }

    public setTextById(elementId: string, value: string) {
        (this.$element.querySelector(`#${elementId}`) as any).innerText = value;
    }

    public setText(elementId: string, value: string) {
        const element = (this.$element.querySelector(`${elementId}`) as any);
        element.innerText = value;
    }

    public scrollToTop() {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

    public addOnClickHandler(cssSelector: string, clickFunction: Function) {
        this.addEventHandler(cssSelector, ['click'], clickFunction);
    }

    public addFieldEventHandler(fieldName: string, eventTypes: string[], update: Function, fieldSelector: string = null) {
        const cssSelector = fieldSelector ?? `#${StringHelper.camelToKebabCase(fieldName)}`;
        this.addEventHandler(cssSelector, eventTypes, update, fieldName);
    }

    public addEventHandler(fieldSelector: string, eventTypes: string[], update: Function, fieldName: string = null) {
        const elements = this.$element.querySelectorAll(fieldSelector) as HTMLInputElement[];
        this.attachFieldListeners(
            elements,
            eventTypes,
            async (el, value, name) => {
                await update(fieldSelector, value, name, el);
            },
            fieldName
        );
    }

    public bind(
        presenter,
        globalHandler: (fieldSelector: string, value: string, fieldName: string) => Promise<void>,
        fieldHandlers?: Record<string, (fieldSelector: string, value: string, fieldName: string) => Promise<void>>
    ) {
        this.bindDataAttributes(globalHandler, fieldHandlers);
        this.bindClickHandlersFromScope(presenter);
        this.bindNavigateHandlersFromScope(presenter);
    }

    public bindDataAttributes(
        globalHandler: (fieldSelector: string, value: string, fieldName: string) => Promise<void>,
        fieldHandlers?: Record<string, (fieldSelector: string, value: string, fieldName: string) => Promise<void>>
    ) {
        const elements = this.$element.querySelectorAll('[data-field]');

        elements.forEach((el: HTMLElement) => {
            const fieldName = el.getAttribute('data-field');
            if (!fieldName) return;

            // read initial value
            if (el instanceof HTMLInputElement)
                this.setValue(fieldName, el.value);

            if (el instanceof HTMLSelectElement)
                this.setValue(fieldName, el.value)

            // default to input,blur if no data-events are provided
            const eventAttr = el.getAttribute('data-events') ?? 'input,blur';
            const eventTypes = eventAttr.split(',').map(e => e.trim());

            this.attachFieldListeners(
                [el],
                eventTypes,
                async (el, value, name) => {
                    const handler = fieldHandlers?.[name] ?? globalHandler;
                    await handler(`#${el.id}`, value, name);
                },
                fieldName
            );

        });
    }

    public bindClickHandlersFromScope(scope: any) {
        const clickableElements = this.$element.querySelectorAll('[data-click]');

        clickableElements.forEach((el: HTMLElement) => {
            const handlerName = el.getAttribute('data-click');
            if (!handlerName) return;

            const method = scope[handlerName];

            el.addEventListener('click', (e: Event) => {
                e.preventDefault();

                if (typeof method === 'function') {
                    method.call(scope, el); // Pass the element optionally
                } else {
                    console.warn(`No method '${handlerName}' found on the provided scope.`);
                }
            });

        });
    }

    public bindNavigateHandlersFromScope(scope: any) {
        const clickableElements = this.$element.querySelectorAll('[data-navigate]');

        clickableElements.forEach((el: HTMLElement) => {
            const navigateUrl = el.getAttribute('data-navigate');
            if (!navigateUrl) return;

            el.addEventListener('click', (e: Event) => {
                e.preventDefault();

                const urlSanitizer = this._dependencyContainer.resolve<UrlSanitizer>('UrlSanitizer');
                const url = urlSanitizer.sanitize(this._router, navigateUrl, true);

                if (url) {
                    //todo: clarify, should this reference _router?
                    window.location.href = url;
                } else {
                    console.warn(`No method '${navigateUrl}' found on the provided scope.`);
                }
            });

        });
    }

    private setValue(name, value) {
        if (name) {
            // only apply if different
            if (value !== this[name + '_$$value_copy']) {
                this[name + '_$$value_copy'] = value;
                this[name] = value;
            }
        }
    }

    private attachFieldListeners(
        elements: HTMLElement[],
        eventTypes: string[],
        callback: (el: HTMLInputElement, value: string, fieldName: string) => Promise<void>,
        fieldName?: string
    ) {
        for (const eventType of eventTypes) {
            for (const el of elements) {
                el.addEventListener(eventType, async (e: Event) => {

                    const inputEl = e.target as HTMLInputElement;

                    const value = inputEl.value;

                    const name = fieldName ?? el.getAttribute('data-field');

                    this.setValue(name, value);

                    await callback(inputEl, value, name);
                });
            }
        }
    }

    public setFieldErrors(errors: ErrorBase[], fieldName: string) {
        const fieldNameKebab = StringHelper.camelToKebabCase(fieldName);

        // remove errors, refine this later
        document.querySelectorAll(`[data-field-name="${fieldNameKebab}"]`).forEach(
            el => el.remove()
        );

        // remove borders

        const fieldErrors = this.getErrorByField(errors, fieldName);

        const targetInputElement = this.$element.querySelector(`#${fieldNameKebab}`);
        if (!targetInputElement) {
            console.log('Could not set field error. Label element for field not found: ' + fieldName);
            return;
        }

        if (fieldErrors.length > 0) {

            targetInputElement.classList.add('field-error-input');

            const errorDiv = document.createElement('div');
            errorDiv.className = 'field-error';
            errorDiv.setAttribute('data-field-name', fieldNameKebab);
            errorDiv.innerHTML = this.renderErrorList(fieldErrors);

            this.insertAfter(errorDiv, targetInputElement);
        } else {
            targetInputElement.classList.remove('field-error-input');
        }

    }

    public focusOnElement(elementId: string) {
        const element = this.$element.querySelector(`#${elementId}`);
        if (element) {
            element.focus();
            element.scrollTo({top: 0, behavior: "smooth"});
        }
    }

    public setErrorList(errors: ErrorBase[]) {
        if (errors.length === 0) {
            this.setErrorListVisibility(false);
        } else {
            const html = this.renderErrorList(errors);
            const element = this.$element.querySelector(`#${this._viewName}-error-list`) as any;
            element.innerHTML = html;
            this.setErrorListVisibility(true);
        }
    }

    public setErrorListVisibility(isVisible: boolean) {
        const element = this.$element.querySelector(`#${this._viewName}-error-list`) as any;
        element.style.display = isVisible ? 'block' : 'none';
        if (isVisible) {
            element.scrollIntoView();
        }
    }

    protected renderErrorList(errors: ErrorBase[]) {
        let element = `<ul>`;
        for (const error of errors) {
            element += `<li>${error.message}</li>`;
        }
        element += `</ul>`;
        return element;
    }

    public getErrorByField(errors: ErrorBase[], field: string) {
        return errors.filter(x => x.fieldName === field);
    }

    private insertAfter(newNode: HTMLElement, referenceNode: HTMLElement) {
        referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    }
}
